// Mirrors normalize_msisdn in the backend's network/validators.py.

const MSISDN_SEPARATORS = /[\s\-().]/g;
const MSISDN_ALLOWED_TYPES = ["MOBILE", "FIXED_LINE_OR_MOBILE", "VOIP"];
const NON_MOBILE_TYPE_WORDS = {
  TOLL_FREE: "toll-free",
  SHARED_COST: "share-call",
  FIXED_LINE: "landline",
};

// Must track ALERT_CONTACTS_DEFAULT_REGION on the backend.
const DEFAULT_REGION = "ZA";

export const MSISDN_DEFAULT_REGION = DEFAULT_REGION;

const MSISDN_ERROR_MESSAGE =
  "Enter a valid mobile number, e.g. 082 123 4567 or +44 7400 123456";

const MSISDN_HELP_TEXT =
  "Mobile number, local or international, e.g. 082 123 4567 or +44 7400 123456";

const MSISDN_DIGITS_ONLY_MESSAGE =
  "Enter digits only, with an optional leading + for the country code.";

const MSISDN_UNKNOWN_COUNTRY_MESSAGE =
  "That country code is not recognised. Pick a country or include the code, e.g. +44 7400 123456";

let phoneNumberUtils = null;
let mobileExamples = null;
let phoneNumberUtilsLoader = null;

export const loadPhoneNumberUtils = () => {
  if (phoneNumberUtils) return Promise.resolve(phoneNumberUtils);
  if (!phoneNumberUtilsLoader) {
    // Must stay on max metadata: min has no per-type patterns, so isValid()
    // degrades to a length check and getType() stops working.
    phoneNumberUtilsLoader = Promise.all([
      import("libphonenumber-js/max"),
      import("libphonenumber-js/examples.mobile"),
    ])
      .then(([loadedModule, loadedExamples]) => {
        phoneNumberUtils = loadedModule;
        mobileExamples = loadedExamples.default || loadedExamples;
        return loadedModule;
      })
      .catch((error) => {
        console.error("Error loading phone number utils:", error);
        phoneNumberUtilsLoader = null;
        return null;
      });
  }
  return phoneNumberUtilsLoader;
};

const stripSeparators = (value) => (value || "").trim().replace(MSISDN_SEPARATORS, "");

const withLeadingPlus = (value) => {
  const cleaned = stripSeparators(value);
  if (!cleaned) return "";
  return cleaned.startsWith("+") ? cleaned : `+${cleaned}`;
};

const msisdnCandidate = (rawValue) => {
  const cleaned = stripSeparators(rawValue);
  if (!cleaned) return "";
  return cleaned.startsWith("00") ? `+${cleaned.slice(2)}` : cleaned;
};

const parseMsisdn = (rawValue, countryCode = DEFAULT_REGION) => {
  if (!phoneNumberUtils) return null;
  const candidate = msisdnCandidate(rawValue);
  if (!candidate) return null;
  if (candidate.startsWith("+")) {
    return phoneNumberUtils.parsePhoneNumberFromString(candidate) || null;
  }
  const asNationalNumber = phoneNumberUtils.parsePhoneNumberFromString(
    candidate,
    countryCode
  );
  if (asNationalNumber && asNationalNumber.isValid()) return asNationalNumber;
  const asInternational = phoneNumberUtils.parsePhoneNumberFromString(`+${candidate}`);
  if (asInternational && asInternational.isValid()) return asInternational;
  return asNationalNumber || asInternational || null;
};

const mobileLengthFloor = (parsed) => {
  if (parsed.country !== DEFAULT_REGION || !mobileExamples) return 0;
  const example = phoneNumberUtils.getExampleNumber(parsed.country, mobileExamples);
  if (!example) return 0;
  return String(example.nationalNumber).length;
};

export const canValidateMsisdn = () => phoneNumberUtils !== null;

export const isValidMsisdn = (rawValue, countryCode = DEFAULT_REGION) => {
  if (/[a-z]/i.test(rawValue || "")) return false;
  const parsed = parseMsisdn(rawValue, countryCode);
  if (!parsed || !parsed.isValid()) return false;
  if (!MSISDN_ALLOWED_TYPES.includes(parsed.getType())) return false;
  return String(parsed.nationalNumber).length >= mobileLengthFloor(parsed);
};

export const normalizeMsisdn = (rawValue, countryCode = DEFAULT_REGION) => {
  if (!isValidMsisdn(rawValue, countryCode)) return null;
  return parseMsisdn(rawValue, countryCode).number.replace(/^\+/, "");
};

export const formatMsisdnForDisplay = (rawValue, countryCode = DEFAULT_REGION) => {
  if (!rawValue) return "";
  const parsed = parseMsisdn(rawValue, countryCode);
  if (parsed && parsed.isValid()) return parsed.formatInternational();
  return withLeadingPlus(rawValue);
};

let regionDisplayNames = null;
let countryOptionsCache = null;

const countryDisplayName = (countryCode) => {
  if (regionDisplayNames === null && typeof Intl.DisplayNames === "function") {
    regionDisplayNames = new Intl.DisplayNames(["en"], { type: "region" });
  }
  if (!regionDisplayNames) return countryCode;
  return regionDisplayNames.of(countryCode) || countryCode;
};

export const listMsisdnCountries = () => {
  if (!phoneNumberUtils) return [];
  if (countryOptionsCache) return countryOptionsCache;
  countryOptionsCache = phoneNumberUtils
    .getCountries()
    .map((countryCode) => {
      const dialCode = phoneNumberUtils.getCountryCallingCode(countryCode);
      return {
        countryCode,
        dialCode,
        label: `${countryDisplayName(countryCode)} (+${dialCode})`,
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label));
  return countryOptionsCache;
};

const mobileExampleForCountry = (countryCode) => {
  if (!phoneNumberUtils || !mobileExamples || !countryCode) return "";
  const example = phoneNumberUtils.getExampleNumber(countryCode, mobileExamples);
  return example ? example.formatNational() : "";
};

export const msisdnHintForCountry = (countryCode) => {
  const example = mobileExampleForCountry(countryCode);
  if (!example) return MSISDN_HELP_TEXT;
  return `${countryDisplayName(countryCode)} mobile number, e.g. ${example}`;
};

export const dialCodeForCountry = (countryCode) => {
  if (!phoneNumberUtils || !phoneNumberUtils.isSupportedCountry(countryCode || "")) return "";
  return `+${phoneNumberUtils.getCountryCallingCode(countryCode)}`;
};

export const splitInternationalMsisdn = (rawValue) => {
  if (!phoneNumberUtils) return null;
  const candidate = msisdnCandidate(rawValue);
  if (!candidate.startsWith("+")) return null;
  const parsed = phoneNumberUtils.parsePhoneNumberFromString(candidate);
  if (!parsed || !parsed.country) return null;
  return { countryCode: parsed.country, nationalNumber: parsed.formatNational() };
};

const tooShortMessage = (countryCode) =>
  `Too short for ${countryDisplayName(countryCode)}. Example: ${mobileExampleForCountry(countryCode)}`;

// Labels a rejection isValidMsisdn has already made; it never decides one.
export const msisdnRejectionReason = (rawValue, pickedCountry = DEFAULT_REGION) => {
  if (/[a-z]/i.test(rawValue || "")) return MSISDN_DIGITS_ONLY_MESSAGE;
  const candidate = msisdnCandidate(rawValue);
  const lengthVerdict = phoneNumberUtils
    ? phoneNumberUtils.validatePhoneNumberLength(candidate, pickedCountry)
    : undefined;
  if (lengthVerdict === "INVALID_COUNTRY" || lengthVerdict === "NOT_A_NUMBER") {
    return MSISDN_UNKNOWN_COUNTRY_MESSAGE;
  }
  const parsed = parseMsisdn(rawValue, pickedCountry);
  if (!parsed) {
    return candidate.startsWith("+")
      ? MSISDN_UNKNOWN_COUNTRY_MESSAGE
      : tooShortMessage(pickedCountry);
  }
  const countryCode = parsed.country || pickedCountry;
  if (lengthVerdict === "TOO_LONG") {
    return `Too long for ${countryDisplayName(countryCode)}. Example: ${mobileExampleForCountry(countryCode)}`;
  }
  if (parsed.isValid() && !MSISDN_ALLOWED_TYPES.includes(parsed.getType())) {
    const rangeWord = NON_MOBILE_TYPE_WORDS[parsed.getType()] || "non-mobile";
    return `This is a ${rangeWord} number, which usually cannot receive alert messages.`;
  }
  // libphonenumber accepts some short numbers the backend's example-length floor
  // rejects, so TOO_SHORT alone does not cover this.
  if (lengthVerdict === "TOO_SHORT" || String(parsed.nationalNumber).length < mobileLengthFloor(parsed)) {
    return tooShortMessage(countryCode);
  }
  return MSISDN_ERROR_MESSAGE;
};
